import { Field, ErrorMessage, FormikProps } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';

interface QuestionTextFieldsProps {
  formik: FormikProps<QuestionFormValues>;
  translateField: (
    sourceKey: string,
    sourceValue: string,
    targetKey: string,
    setFieldValue: any
  ) => void;
  isFieldTranslating: (key: string) => boolean;
  isEditable: boolean;
  language?: 'en' | 'fr';
}

export function QuestionTextFields({
  formik,
  translateField,
  isFieldTranslating,
  isEditable,
  language,
}: QuestionTextFieldsProps) {
  const { errors, touched, values, setFieldValue } = formik;

  // If language is specified, show only that language's field
  if (language === 'en') {
    return (
      <div className="flex flex-col gap-2">
        <TranslatableFieldLabel
          htmlFor="question_text_en"
          onTranslate={() =>
            translateField(
              'question_text_en',
              values.question_text_en,
              'question_text_fr',
              setFieldValue
            )
          }
          isTranslating={isFieldTranslating('question_text_en')}
        >
          Question Text
        </TranslatableFieldLabel>
        <Field
          as={Input}
          id="question_text_en"
          name="question_text_en"
          data-test-id="question-question_text_en"
          placeholder="Enter the question in English"
          disabled={!isEditable}
          className={errors.question_text_en && touched.question_text_en ? 'border-red-500' : ''}
        />
        <ErrorMessage name="question_text_en">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    );
  }

  if (language === 'fr') {
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor="question_text_fr">Question Text</Label>
        <Field
          as={Input}
          id="question_text_fr"
          name="question_text_fr"
          data-test-id="question-question_text_fr"
          placeholder="Enter the question in French"
          disabled={!isEditable}
          className={errors.question_text_fr && touched.question_text_fr ? 'border-red-500' : ''}
        />
        <ErrorMessage name="question_text_fr">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    );
  }

  // Fallback: show both languages side-by-side (for backward compatibility)
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="flex flex-col gap-2">
        <TranslatableFieldLabel
          htmlFor="question_text_en"
          onTranslate={() =>
            translateField(
              'question_text_en',
              values.question_text_en,
              'question_text_fr',
              setFieldValue
            )
          }
          isTranslating={isFieldTranslating('question_text_en')}
        >
          Question Text (English)
        </TranslatableFieldLabel>
        <Field
          as={Input}
          id="question_text_en"
          name="question_text_en"
          data-test-id="question-question_text_en"
          placeholder="Enter the question in English"
          disabled={!isEditable}
          className={errors.question_text_en && touched.question_text_en ? 'border-red-500' : ''}
        />
        <ErrorMessage name="question_text_en">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="question_text_fr">Question Text (French)</Label>
        <Field
          as={Input}
          id="question_text_fr"
          name="question_text_fr"
          data-test-id="question-question_text_fr"
          placeholder="Enter the question in French"
          disabled={!isEditable}
          className={errors.question_text_fr && touched.question_text_fr ? 'border-red-500' : ''}
        />
        <ErrorMessage name="question_text_fr">
          {msg => <span className="text-sm text-red-500">{msg}</span>}
        </ErrorMessage>
      </div>
    </div>
  );
}
